// /modules/vehiculos/components/BusquedaUnidadTab.jsx
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  Chip,
  Button,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  TextField,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import PublicIcon from "@mui/icons-material/Public";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import BuscarVehiculo from "./BuscarVehiculo";
import BusquedasRecientes, { useBusquedasRecientes } from "../../requerimiento/components/BusquedasRecientes";
import { buscarVehiculoGlobal, copiarUnidadWialonALeasecontrol } from "../services/vehiculosService";
import { lcListHwTypes } from "../../leasecontrol/services/leasecontrolUsuarios.service";
import VehiculoDetalleDialog from "./VehiculoDetalleDialog";
import ReclamoModal from "../../reclamos/components/ReclamoModal";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";
import ConfirmDeleteDialog from "../../leasecontrol/components/ConfirmDeleteDialog";
import { eliminarVehiculo as eliminarVehiculoTrs } from "../../trsrastreo/services/trsRastreoService";

const coloresFuente = {
  "YPFB internacional": "primary",
  "YPFB nacional": "secondary",
  rastreo: "success",
  rastreo_bo: "info",
  flespi: "warning",
  wialon: "error",
  SORT: "success",
};
const iconosFuente = {
  "YPFB internacional": <LocalShippingIcon fontSize="small" />,
  "YPFB nacional": <LocalShippingIcon fontSize="small" />,
  rastreo: <GpsFixedIcon fontSize="small" />,
  rastreo_bo: <GpsFixedIcon fontSize="small" />,
  flespi: <SatelliteAltIcon fontSize="small" />,
  wialon: <PublicIcon fontSize="small" />,
};

const getRazon = (v) => {
  const extras = v?.extras || {};
  if (extras.vehAlias) return extras.vehAlias;
  if (extras.CLINOMBRE) return extras.CLINOMBRE;
  const txt = `${extras.nombre_normal || ""} ${extras.descripcion_normal || ""}`.trim();
  return txt || "—";
};

const getVelocidad = (v) => {
  const extras = v?.extras || {};
  const val = extras.vehVelocidadU ?? extras?.telemetry?.position?.speed;
  return val === undefined || val === null || val === "" ? null : Number(val);
};

const getPuntoCercano = (v) => {
  const extras = v?.extras || {};
  return (
    extras.vehPuntoCercanoU ||
    extras.VEHPUNTOCERCANOu ||
    extras?.telemetry?.["wialon.address"] ||
    "—"
  );
};

const cellSx = { py: 0.3, px: 0.75, fontSize: "0.72rem" };

export default function BusquedaUnidadTab({ valorInicial } = {}) {
  const [valor, setValor] = useState("");
  const [resultado, setResultado] = useState(null); // { data: [...] }
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [equipoDialog, setEquipoDialog] = useState(null);

  const vehiculos = resultado?.data ?? [];
  const seleccionado = selectedIndex != null ? vehiculos[selectedIndex] : null;

  const [openReclamo, setOpenReclamo] = useState(false);
  const [reclamoTitle, setReclamoTitle] = useState("");
  const [reclamoData, setReclamoData] = useState(null);
  const [showReclamoOk, setShowReclamoOk] = useState(false);

  // Copiar unidad de Wialon a Leasecontrol
  const [copiar, setCopiar] = useState(null); // { v }
  const [copiando, setCopiando] = useState(false);
  const [copiarError, setCopiarError] = useState("");
  const [copiarOk, setCopiarOk] = useState("");
  const [hwPendiente, setHwPendiente] = useState(null); // { hwWialon } cuando no hay equivalente
  const [hwOpciones, setHwOpciones] = useState([]);
  const [hwSeleccionado, setHwSeleccionado] = useState(null);
  const [hwBusqueda, setHwBusqueda] = useState("");

  // Eliminar unidad (solo rastreo_bo / TRS Rastreo)
  const [eliminar, setEliminar] = useState(null); // fila de resultados | null
  const [eliminando, setEliminando] = useState(false);
  const [eliminarError, setEliminarError] = useState("");
  const [eliminarOk, setEliminarOk] = useState("");

  const confirmarEliminar = async () => {
    const v = eliminar;
    if (!v) return;
    setEliminando(true);
    setEliminarError("");
    try {
      await eliminarVehiculoTrs(v.uid);
      // Se quita la fila de los resultados que ya se muestran, sin volver a buscar.
      setResultado((prev) => (prev ? { ...prev, data: (prev.data || []).filter((x) => x !== v) } : prev));
      setEliminarOk(`Unidad ${v.placa || v.alias || v.uid} eliminada de TRS Rastreo.`);
      setEliminar(null);
    } catch (err) {
      setEliminarError(err?.response?.data?.error || err?.message || "No se pudo eliminar la unidad.");
    } finally {
      setEliminando(false);
    }
  };

  const cerrarCopiar = () => {
    if (copiando) return;
    setCopiar(null);
    setCopiarError("");
    setHwPendiente(null);
    setHwSeleccionado(null);
    setHwBusqueda("");
  };

  useEffect(() => {
    if (!hwPendiente) return undefined;
    let activo = true;
    const t = setTimeout(() => {
      lcListHwTypes({ search: hwBusqueda, limit: 50 })
        .then((res) => { if (activo) setHwOpciones(res?.data?.data || []); })
        .catch(() => { if (activo) setHwOpciones([]); });
    }, 300);
    return () => { activo = false; clearTimeout(t); };
  }, [hwPendiente, hwBusqueda]);

  const confirmarCopia = async () => {
    const v = copiar?.v;
    if (!v) return;
    setCopiando(true);
    setCopiarError("");
    try {
      const { data } = await copiarUnidadWialonALeasecontrol(v.uid, hwSeleccionado?.id);
      setCopiarOk(`Unidad "${data?.unidad?.nm || v.placa}" copiada a Leasecontrol (${data?.hwType?.name || "tipo elegido"}).`);
      setCopiar(null);
      setHwPendiente(null);
      setHwSeleccionado(null);
    } catch (err) {
      const resp = err?.response?.data;
      if (err?.response?.status === 422 && resp?.requiereHwType) {
        setHwPendiente({ hwWialon: resp.hwWialon });
        setCopiarError(resp.error);
      } else {
        const msg = resp?.error;
        setCopiarError(typeof msg === "string" ? msg : "No se pudo copiar la unidad a Leasecontrol.");
      }
    } finally {
      setCopiando(false);
    }
  };

  const handleBuscar = async (valorOverride) => {
    const term = (valorOverride ?? valor).trim();
    if (!term) {
      setError("Debe ingresar un valor válido.");
      return;
    }
    setCargando(true);
    setError(null);
    setSelectedIndex(null);
    setDetalleOpen(false);

    try {
      const data = await buscarVehiculoGlobal(term);
      setResultado(data);
    } catch (err) {
      console.error(err);
      setError("Error al buscar vehículo.");
    } finally {
      setCargando(false);
    }
  };

  // Últimas búsquedas (por navegador): se reutiliza el panel compartido; aquí solo importa el texto buscado (guardado como nombreCliente).
  const { recientes, buscar: buscarYRecordar, repetir, quitar, limpiar } = useBusquedasRecientes(
    "vehiculos_busquedas_recientes",
    (f) => { setValor(f.nombreCliente); handleBuscar(f.nombreCliente); }
  );

  // Permite llegar desde la busqueda global del navbar con ?q=<valor> ya
  // precargado y disparado, sin que el usuario tenga que volver a tipearlo.
  // `valorInicial` cubre el mismo caso cuando el componente se embebe
  // directo en un dialog (sin navegar, sin query param en la URL).
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const q = valorInicial ?? searchParams.get("q");
    if (q) {
      setValor(q);
      handleBuscar(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verDetalle = (idx) => {
    setSelectedIndex(idx);
    setDetalleOpen(true);
  };

  const abrirReclamo = (v) => {
    const titulo = v?.placa && v.placa !== "" ? `${v.placa}` : `${v?.modem ?? "sin-módem"}`;
    setReclamoTitle(titulo);
    setReclamoData({ placa: v?.placa ?? null, modem: v?.modem ?? null });
    setOpenReclamo(true);
  };

  return (
    <Box>
      {/* Buscador */}
      <Paper variant="outlined" sx={{ p: 0.75, mb: 1 }}>
        <BuscarVehiculo
          valor={valor}
          setValor={setValor}
          handleBuscar={() => buscarYRecordar({ nombreCliente: valor })}
          cargando={cargando}
        />
      </Paper>

      {/* Mensajes */}
      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0.25 }}>
          {error}
        </Alert>
      )}
      {cargando && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 1.5 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!resultado && !cargando && (
        <Box sx={{ mb: 1 }}>
          <BusquedasRecientes
            titulo="Busca un vehículo"
            ayuda="Escribe la placa, UID, alias o código del módem y presiona Enter."
            recientes={recientes} onRepetir={repetir} onQuitar={quitar} onLimpiar={limpiar}
          />
        </Box>
      )}

      {/* Tabla de resultados */}
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Typography
          sx={{
            px: 1,
            py: 0.5,
            fontSize: "0.72rem",
            fontWeight: 800,
            bgcolor: (theme) => (theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.grey[100]),
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          Resultados ({vehiculos.length})
        </Typography>

        <TableContainer>
          <Table
            size="small"
            aria-label="tabla de resultados de vehículos"
            sx={{ "& .MuiTableCell-root": { whiteSpace: "nowrap" } }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...cellSx, fontWeight: 800 }}>Placa</TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 800 }}>Alias</TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 800 }}>Módem</TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 800 }}>Razón / Cliente</TableCell>
                <TableCell align="center" sx={{ ...cellSx, fontWeight: 800 }}>Vel.</TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 800 }}>Punto cercano</TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 800 }}>Fuente</TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 800 }}>Última fecha</TableCell>
                <TableCell align="center" sx={{ ...cellSx, fontWeight: 800 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vehiculos.map((v, idx) => {
                const fuenteColor = coloresFuente[v.fuente] || "default";
                const iconoFuente = iconosFuente[v.fuente] || null;
                const razon = getRazon(v);
                const velocidad = getVelocidad(v);
                const puntoCercano = getPuntoCercano(v);
                const esSort = v.fuente === "SORT";
                return (
                  <TableRow
                    key={`${v.placa ?? "no-placa"}-${idx}`}
                    hover
                    sx={esSort ? { bgcolor: (theme) => alpha(theme.palette.success.main, 0.08) } : undefined}
                  >
                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }} noWrap>
                        {v.placa ?? "—"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: "0.72rem" }} color="text.secondary" noWrap>
                        {v.alias ?? "—"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: "0.72rem" }} color="text.secondary" noWrap>
                        {v.modem ? (
                          <span
                            onClick={() => setEquipoDialog({ tipo: "modem", identificador: v.modem })}
                            style={{
                              color: "#1976d2",
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                          >
                            {v.modem}
                          </span>
                        ) : (
                          "—"
                        )}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ ...cellSx, maxWidth: 200, whiteSpace: "normal" }}>
                      <Typography
                        noWrap
                        title={razon}
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          display: "block",
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {razon}
                      </Typography>
                      {(v?.extras?.nombre_ypfb || v?.extras?.descripcion_ypfb) && (
                        <Typography
                          noWrap
                          color="text.secondary"
                          title={`${v.extras.nombre_ypfb || ""} ${v.extras.descripcion_ypfb || ""}`.trim()}
                          sx={{ fontSize: "0.68rem", display: "block", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                          {`${v.extras.nombre_ypfb || ""} ${v.extras.descripcion_ypfb || ""}`.trim()}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center" sx={cellSx}>
                      <Chip
                        size="small"
                        label={velocidad === null ? "—" : `${velocidad} km/h`}
                        color={velocidad ? "primary" : "default"}
                        variant={velocidad ? "filled" : "outlined"}
                        sx={{ height: 18, fontSize: "0.65rem" }}
                      />
                    </TableCell>

                    <TableCell sx={{ ...cellSx, maxWidth: 220, whiteSpace: "normal" }}>
                      <Typography
                        noWrap
                        color="text.secondary"
                        title={puntoCercano}
                        sx={{
                          fontSize: "0.7rem",
                          display: "block",
                          maxWidth: 220,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {puntoCercano}
                      </Typography>
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <Chip
                        size="small"
                        label={v.fuente ?? "—"}
                        color={fuenteColor}
                        icon={iconoFuente}
                        sx={{ height: 18, fontSize: "0.65rem" }}
                      />
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <Typography sx={{ fontSize: "0.7rem" }} color="text.secondary" noWrap>
                        {v.fecha_ultimo_registro || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell align="center" sx={cellSx}>
                      <Tooltip title="Ver más información">
                        <IconButton size="small" color="primary" onClick={() => verDetalle(idx)} sx={{ p: 0.4 }}>
                          <VisibilityIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Emitir reclamo">
                        <IconButton size="small" color="warning" onClick={() => abrirReclamo(v)} sx={{ p: 0.4 }}>
                          <ReportProblemIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      {/* Solo si no tiene usuarios vinculados directo (si está en un grupo, TRS Rastreo lo rechaza y se muestra el error) */}
                      {v.fuente === "rastreo_bo" && v.uid && !(v.extras?.usuarios_relacionados || []).length && (
                        <Tooltip title="Eliminar unidad de TRS Rastreo">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => { setEliminarError(""); setEliminar(v); }}
                            sx={{ p: 0.4 }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {v.fuente === "wialon" && (
                        <Tooltip title="Copiar a Leasecontrol">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => { setCopiarError(""); setCopiar({ v }); }}
                            sx={{ p: 0.4 }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}

              {vehiculos.length === 0 && resultado !== null && !cargando && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={cellSx}>
                    <Typography sx={{ fontSize: "0.75rem" }} color="text.secondary">
                      No se encontraron vehículos.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <ConfirmDeleteDialog
        open={!!eliminar}
        loading={eliminando}
        titulo="Eliminar unidad"
        onClose={() => setEliminar(null)}
        description={
          eliminarError
            || `¿Eliminar la unidad ${eliminar?.placa || eliminar?.alias || ""} de TRS Rastreo? Esta acción no se puede deshacer.`
        }
        onConfirm={confirmarEliminar}
      />
      <Snackbar
        open={!!eliminarOk}
        autoHideDuration={4000}
        onClose={() => setEliminarOk("")}
        message={eliminarOk}
      />

      {/* Dialog: detalle de la unidad seleccionada */}
      <VehiculoDetalleDialog
        open={detalleOpen}
        onClose={() => setDetalleOpen(false)}
        vehiculo={seleccionado}
        razon={seleccionado ? getRazon(seleccionado) : null}
        velocidad={seleccionado ? getVelocidad(seleccionado) : null}
        puntoCercano={seleccionado ? getPuntoCercano(seleccionado) : null}
        fuenteColor={coloresFuente[seleccionado?.fuente] || "default"}
        fuenteIcono={iconosFuente[seleccionado?.fuente] || null}
        onReclamo={abrirReclamo}
        onVerModem={(modem) => setEquipoDialog({ tipo: "modem", identificador: modem })}
      />

      {/* Dialog: copiar unidad de Wialon a Leasecontrol */}
      <Dialog open={!!copiar} onClose={cerrarCopiar} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>Copiar a Leasecontrol</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Typography variant="body2">
              Se creará en Leasecontrol la unidad <strong>{copiar?.v?.placa}</strong> con el UID{" "}
              <strong>{copiar?.v?.modem || "—"}</strong>{copiar?.v?.alias ? ` y el alias ${copiar.v.alias}` : ""}.
              Solo se da de alta en la plataforma: no se modifica el módem ni el chip en SORT.
            </Typography>
            {copiarError && <Alert severity={hwPendiente ? "warning" : "error"} sx={{ py: 0.25 }}>{copiarError}</Alert>}
            {hwPendiente && (
              <Autocomplete
                size="small"
                options={hwOpciones}
                value={hwSeleccionado}
                onChange={(_, val) => setHwSeleccionado(val)}
                onInputChange={(_, val, reason) => { if (reason === "input") setHwBusqueda(val); }}
                getOptionLabel={(o) => o?.name || ""}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                filterOptions={(x) => x}
                renderInput={(params) => (
                  <TextField {...params} label={`Tipo de dispositivo (en Wialon: ${hwPendiente.hwWialon || "—"})`} />
                )}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 0.75 }}>
          <Button size="small" onClick={cerrarCopiar} disabled={copiando}>Cancelar</Button>
          <Button
            size="small"
            variant="contained"
            onClick={confirmarCopia}
            disabled={copiando || (!!hwPendiente && !hwSeleccionado)}
          >
            {copiando ? <CircularProgress size={16} color="inherit" /> : "Copiar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!copiarOk}
        autoHideDuration={5000}
        onClose={() => setCopiarOk("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setCopiarOk("")} severity="success" variant="filled" sx={{ width: "100%" }}>
          {copiarOk}
        </Alert>
      </Snackbar>

      <ReclamoModal
        open={openReclamo}
        onClose={() => setOpenReclamo(false)}
        initialTitle={reclamoTitle}
        initialData={{
          placa: reclamoData?.placa ?? null,
          modem: reclamoData?.modem ?? null,
        }}
        onSuccessNavigate={(id) => {
          console.log("Reclamo creado", id);
          setOpenReclamo(false);
        }}
      />
      {/* snackbar de confirmación */}
      <Snackbar
        open={showReclamoOk}
        autoHideDuration={4000}
        onClose={() => setShowReclamoOk(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setShowReclamoOk(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Reclamo enviado correctamente.
        </Alert>
      </Snackbar>

      {/* Detalle del módem: reutiliza el componente de /centro-equipos (Búsqueda por Identificador) */}
      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo || "modem"}
        identificador={equipoDialog?.identificador}
        title="Detalle del módem"
        onClose={() => setEquipoDialog(null)}
      />
    </Box>
  );
}
